import React, { useEffect, useRef } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Folder, FolderOpen, Options, Plus, Triangle } from 'components/icons'
import Tooltip from 'components/tooltip'
import {
  SIDEBAR_ROW,
  SIDEBAR_ROW_IDLE,
  SIDEBAR_ICON,
  SIDEBAR_SLOT,
  sidebarLabel,
  rowHighlightStyle,
  indentFor,
  SIDEBAR_DROP_LINE,
  dropLineStyle,
  SelectionJoin
} from 'components/sidebar/surface'
import { rowButtonProps } from 'components/sidebar/rowButton'
import { SelectionModifiers } from 'components/sidebar/components/folders/selection'
import { DropZone } from 'components/sidebar/components/folders/tree'
import { emptyFolderDropId } from 'components/sidebar/components/folders/dnd'
import {
  ROW_ACTIONS,
  ROW_ACTIONS_ROOM,
  RenameInput,
  RenameResult,
  RowAction
} from 'components/sidebar/components/folders/rowParts'

/**
 * The folder, open while its contents show, in the icon slot every row has, so
 * its icon lines up with the documents' and tables' beside it. Its disclosure
 * triangle hangs in the margin to its left, smaller, outside that column: a
 * 12px glyph ending just before the folder's (the slot's 6px padding, less a
 * pixel, puts its left edge 6px before the slot).
 */
const FolderChevron: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <span className={`${SIDEBAR_ICON} relative`} data-testid="sidebar-folder-icon" data-open={expanded}>
    <span
      className="absolute -left-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-3 [&>svg]:w-3 [&>svg]:h-3"
      data-testid="sidebar-folder-chevron"
      aria-hidden
    >
      <Triangle type={expanded ? 'down' : 'right'} />
    </span>
    {expanded ? <FolderOpen /> : <Folder />}
  </span>
)

interface FolderRowContentProps {
  name: string
  expanded: boolean
  onNameDoubleClick?: () => void
}

/** A folder row's look, the chevron, icon and name, with no behaviour, so a drag preview can reuse it. */
export const FolderRowContent: React.FC<FolderRowContentProps> = ({ name, expanded, onNameDoubleClick }) => (
  <>
    <FolderChevron expanded={expanded} />
    <Tooltip content={name} showOnlyIfTruncated delay={300}>
      <span className={sidebarLabel()} onDoubleClick={onNameDoubleClick}>
        {name}
      </span>
    </Tooltip>
  </>
)

interface FolderRowProps {
  id: string
  name: string
  depth: number
  expanded: boolean
  /** Whether the viewer may manage folders (hub Owners): rename, drag, and add subfolders. */
  canManage: boolean
  /** Whether a drag may drop into or beside this row (anyone who can move something). */
  droppable: boolean
  /**
   * Showing the inline rename box. Controlled by the tree, so a folder can also open straight into
   * rename (a newly created one, or the context menu's Rename).
   */
  renaming: boolean
  /** Part of the sidebar's multi-selection. */
  selected: boolean
  /** Which neighbours are selected too, so a run of selected rows draws as one block. */
  selectionJoin?: SelectionJoin
  /** What a folder's Delete & archive would take away, while it's pointed at: a red wash. */
  previewed?: boolean
  /** A drag would drop into this folder (`into`), or beside it (a line on that edge). */
  dropIndicator?: DropZone | null
  /** Part of the drag in progress. */
  dragging?: boolean
  brandColour?: string
  onToggle: (id: string) => void
  onRenameStart: (id: string) => void
  /** The rename box closed, and how. */
  onRenameEnd: (id: string, result: RenameResult) => void
  /** Runs before the row toggles. Returning true claims the click (a selection gesture). */
  onSelectClick: (event: SelectionModifiers, id: string) => boolean
  /** A right-click on the row; `dragging` while a drag is on. */
  onContextMenu?: (event: React.MouseEvent<HTMLElement>, id: string, dragging: boolean) => void
  /** Opens the create menu for this folder from its +. Unset for viewers who can't create anything here. */
  onNewIn?: (id: string, button: HTMLElement) => void
  /** Opens the row's menu from its ⋯. Unset for viewers with no menu here. */
  onOpenMenu?: (id: string, button: HTMLElement) => void
}

/**
 * A held-down key's repeats, or the second click of a double-click: neither should toggle again.
 * Narrowed on the event type because a keyboard activation has no click count to read.
 */
type RowActivation = React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>

const isRepeat = (event: RowActivation): boolean =>
  event.type === 'keydown' ? (event as React.KeyboardEvent).repeat : (event as React.MouseEvent).detail > 1

/**
 * A folder in the sidebar tree: a disclosure row whose children the tree renders beneath it.
 * Memoised, as ResourceRow is, so a change to one row doesn't re-render the rest.
 */
const FolderRow: React.FC<FolderRowProps> = ({
  id,
  name,
  depth,
  expanded,
  canManage,
  droppable,
  renaming,
  selected,
  selectionJoin = 'none',
  previewed = false,
  dropIndicator = null,
  dragging = false,
  brandColour,
  onToggle,
  onRenameStart,
  onRenameEnd,
  onSelectClick,
  onContextMenu,
  onNewIn,
  onOpenMenu
}) => {
  // The row itself, for focus after a rename.
  const rowRef = useRef<HTMLDivElement>(null)
  const refocus = useRef(false)
  // No dragging out of a rename: the pointer and keys belong to the text box.
  const canDrag = canManage && !renaming
  const drag = useDraggable({ id, disabled: !canDrag })
  const { setNodeRef: setDropRef } = useDroppable({ id, disabled: !droppable })
  // A drag is on (this row's or another's). Its keys are the drag's, not the row's.
  const dragActive = drag.active !== null
  const setRowRef = (node: HTMLDivElement | null): void => {
    rowRef.current = node
    drag.setNodeRef(node)
    setDropRef(node)
  }

  // A rename closed from the keyboard hands focus back to the row. It has to wait for the render
  // that makes the row focusable again.
  useEffect(() => {
    if (renaming || !refocus.current) return
    refocus.current = false
    rowRef.current?.focus()
  }, [renaming])

  const dropTarget = dropIndicator === 'into'
  // No buttons beside a rename box.
  const newIn = renaming ? undefined : onNewIn
  const openMenu = renaming ? undefined : onOpenMenu
  const actionCount = Number(newIn !== undefined) + Number(openMenu !== undefined)

  // A double-click to rename opens or closes the folder once, on its first click, as in Finder.
  const activate = (event: RowActivation): void => {
    if (isRepeat(event)) return
    if (onSelectClick(event, id)) return
    onToggle(id)
  }

  const button = rowButtonProps(activate)
  // While renaming the row is a plain container: a text box inside a button isn't valid. Only
  // dnd-kit's listeners and its keyboard instructions are taken from it: its `attributes` would
  // replace the row's role with its own and add aria-pressed.
  const interactive = renaming
    ? {}
    : {
        ...button,
        'aria-expanded': expanded,
        'aria-describedby': canDrag ? drag.attributes['aria-describedby'] : undefined,
        onPointerDown: drag.listeners?.onPointerDown,
        onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
          // Alt/Option + ↑/↓ picks the row up (SidebarDnd's keyboard sensor).
          drag.listeners?.onKeyDown?.(event)
          if (dragActive) return
          if (event.key === 'F2' && canManage) {
            event.preventDefault()
            onRenameStart(id)
            return
          }
          button.onKeyDown(event)
        }
      }

  return (
    <div className="relative group">
      <div
        ref={setRowRef}
        className={`${SIDEBAR_ROW} ${SIDEBAR_ROW_IDLE} relative ${ROW_ACTIONS_ROOM[actionCount]} ${dragging ? 'opacity-50' : ''}`}
        style={{
          paddingLeft: indentFor(depth),
          ...rowHighlightStyle({ selected, previewed, dropTarget, join: selectionJoin }, brandColour)
        }}
        data-testid="sidebar-folder-row"
        data-row-id={id}
        data-selected={selected}
        data-drop-target={dropTarget}
        data-drop-indicator={dropIndicator ?? undefined}
        data-draggable={canDrag}
        onContextMenu={onContextMenu ? (event) => onContextMenu(event, id, dragActive) : undefined}
        {...interactive}
      >
        {dropIndicator === 'before' || dropIndicator === 'after' ? (
          <span
            className={SIDEBAR_DROP_LINE}
            // An open folder's bottom edge drops at the top of its contents, so the line sits a level in.
            style={dropLineStyle(
              dropIndicator,
              indentFor(dropIndicator === 'after' && expanded ? depth + 1 : depth),
              brandColour
            )}
          />
        ) : null}
        {renaming ? (
          <>
            <FolderChevron expanded={expanded} />
            <RenameInput
              initialName={name}
              label="Folder name"
              testId="folder-rename-input"
              onDone={(result, byKey) => {
                refocus.current = byKey
                onRenameEnd(id, result)
              }}
            />
          </>
        ) : (
          <FolderRowContent
            name={name}
            expanded={expanded}
            onNameDoubleClick={canManage ? () => onRenameStart(id) : undefined}
          />
        )}
      </div>
      {actionCount > 0 && (
        // Beside the row rather than inside it, so they're never part of the row's button, its drag
        // or its toggle.
        <span className={ROW_ACTIONS}>
          {openMenu && (
            <RowAction
              tooltip="More actions"
              label={`More actions for ${name}`}
              testId="sidebar-row-more"
              onClick={(button) => openMenu(id, button)}
            >
              <Options />
            </RowAction>
          )}
          {newIn && (
            <RowAction
              tooltip="New in folder"
              label={`New in ${name}`}
              testId="folder-new-in"
              onClick={(button) => newIn(id, button)}
            >
              <Plus />
            </RowAction>
          )}
        </span>
      )}
    </div>
  )
}

interface EmptyFolderRowProps {
  /** The folder it's inside. */
  folderId: string
  depth: number
  /** Whether a drag may drop on it, into its folder (anyone who can move something). */
  droppable: boolean
}

/**
 * Stands in for the children of an open folder that has none. A drop on it goes into the folder, as
 * a drop on the folder row's middle does, rather than falling through to the tree behind it.
 */
export const EmptyFolderRow: React.FC<EmptyFolderRowProps> = ({ folderId, depth, droppable }) => {
  const { setNodeRef } = useDroppable({ id: emptyFolderDropId(folderId), disabled: !droppable })
  return (
    <div
      ref={setNodeRef}
      className={`${SIDEBAR_ROW} relative cursor-default text-faint`}
      style={{ paddingLeft: indentFor(depth) }}
      data-testid="sidebar-empty-folder"
    >
      {/* An empty icon slot, as wide as every row's, so "Empty" starts where the names do. */}
      <span className={SIDEBAR_SLOT} aria-hidden />
      <span className={`${sidebarLabel()} italic`}>Empty</span>
    </div>
  )
}

export default React.memo(FolderRow)
